import { parseBoard, type Piece } from '@bioforge/game-core';
import type { BossId, PublicBoard, PublicPlayer } from '@bioforge/protocol';
import { Container, Graphics, Rectangle, Text } from 'pixi.js';
import { TouchPad, attachGestures, type TouchCommand } from '../input/touch.js';
import {
  ABILITY_NAMES,
  BOSS_DAMAGE_HINTS,
  BOSS_NAMES,
  BOSS_TAUNTS,
  type Taunt,
} from '../labels.js';
import { idle } from '../ui/motion.js';
import { artSprite } from '../ui/textures.js';
import { COLOR, textStyle } from '../ui/theme.js';
import { keyCap, panel, speechBubble, vertical } from '../ui/widgets.js';
import { formatScore } from './battle.js';
import { abilityRing, capsuleRow, caps, hpBar } from './battle-panels.js';
import { jarModelOf } from './jar-model.js';
import { JarView, jarSize, type JarSkin } from './jar-view.js';
import { raceProgress } from './match.js';

/** Активная капсула из публичного поля. */
export function pieceOf(board: PublicBoard): Piece | null {
  return board.active
    ? { ...board.active, colors: [board.active.colors[0], board.active.colors[1]] }
    : null;
}

export const isBossId = (id: string | undefined | null): id is BossId =>
  typeof id === 'string' && id in BOSS_NAMES;

/** Банка из публичного поля игрока; ячейка — по месту, отведённому на экране. */
export function renderBoardJar(jar: JarView, board: PublicBoard, skin: JarSkin | null): void {
  jar.render(
    jarModelOf({
      board: parseBoard(board.rows),
      active: pieceOf(board),
      boss: board.boss,
      frost: board.effects.frozen > 0,
      skin,
    }),
  );
}

/** Наибольшая клетка, при которой банка помещается в отведённый прямоугольник. */
export function fitCell(
  cols: number,
  rows: number,
  width: number,
  height: number,
  max: number,
): number {
  const empty = jarSize(0, 0, 0);
  return Math.max(
    12,
    Math.min(
      max,
      Math.floor((width - empty.width) / cols),
      Math.floor((height - empty.height) / rows),
    ),
  );
}

export interface DuelData {
  readonly mine: PublicBoard;
  readonly theirs: PublicBoard | null;
  readonly me: PublicPlayer | null;
  readonly opponent: PublicPlayer | null;
  readonly skin: JarSkin | null;
}

interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

const MY_JAR: Rect = { x: 330, y: 146, w: 320, h: 840 - 146 };
const THEIR_JAR: Rect = { x: 890, y: 130, w: 250, h: 840 - 130 };
/** Телефон: своя банка крупно слева, банка соперника уменьшена справа (SPEC.md, 16.3). */
const MY_JAR_TALL: Rect = { x: 22, y: 240, w: 498, h: 780 };
const THEIR_JAR_TALL: Rect = { x: 530, y: 240, w: 168, h: 380 };

/** Управление матчем с касаний: команды капсулы и способности по слотам. */
export interface NetControls {
  readonly portrait: boolean;
  readonly onCommand: (command: TouchCommand) => void;
  readonly onAbility: (slot: 0 | 1) => void;
}

/** Экранные кнопки телефона; `center` — пустое место в середине верхнего ряда. */
export function matchPad(controls: NetControls, center: boolean): TouchPad {
  const send = (type: TouchCommand) => () => controls.onCommand(type);
  const w = center ? 124 : 156;
  return new TouchPad(
    [
      [
        {
          icon: 'icon-pad-left',
          title: 'Влево',
          width: w,
          repeat: true,
          onPress: send('move-left'),
        },
        {
          icon: 'icon-pad-right',
          title: 'Вправо',
          width: w,
          repeat: true,
          onPress: send('move-right'),
        },
        ...(center
          ? [{ icon: '', title: '', width: 112, spacer: true, onPress: () => undefined }]
          : []),
        {
          icon: 'icon-pad-ccw',
          title: 'Повернуть против часовой',
          width: w,
          onPress: send('rotate-counterclockwise'),
        },
        {
          icon: 'icon-pad-cw',
          title: 'Повернуть по часовой',
          width: w,
          onPress: send('rotate-clockwise'),
        },
      ],
      [
        {
          icon: 'icon-pad-down',
          label: 'Ускорить',
          title: 'Ускорить',
          width: 330,
          repeat: true,
          onPress: send('soft-drop'),
        },
        {
          icon: 'icon-pad-drop',
          label: 'Сбросить',
          title: 'Сбросить',
          width: 330,
          onPress: send('hard-drop'),
        },
      ],
    ],
    720,
    96,
  );
}

/** Плитка сводки телефона: подпись и значение. */
export function hudTile(
  x: number,
  w: number,
  title: string,
  value: string,
  color: number = COLOR.mint,
): Container {
  const box = panel({ width: w, height: 84, radius: 16 });
  box.position.set(x, 96);
  const t = caps(title, color);
  t.position.set(14, 12);
  const v = new Text({
    text: value,
    style: textStyle({ size: 24, display: true, weight: '900', shadow: 3 }),
  });
  v.position.set(14, 34);
  if (v.width > w - 28) v.scale.set((w - 28) / v.width);
  box.addChild(t, v);
  return box;
}

/**
 * Дуэль по макету «Дуэль · свой босс у каждого»: слева ваши очки, босс и способности,
 * по центру своя банка и гонка за боссом, справа — банка соперника и его эффекты.
 */
export class DuelView extends Container {
  private myJar: JarView | null = null;
  private theirJar: JarView | null = null;
  private readonly panels = new Container();
  private readonly jars = new Container();
  private readonly stops: (() => void)[] = [];
  private panelsKey = '';
  private lastHp: number | null = null;
  private taunt: Taunt = 'start';
  private stopGestures: (() => void) | null = null;

  private readonly myRect: Rect;
  private readonly theirRect: Rect;

  constructor(private readonly controls: NetControls) {
    super();
    this.myRect = controls.portrait ? MY_JAR_TALL : MY_JAR;
    this.theirRect = controls.portrait ? THEIR_JAR_TALL : THEIR_JAR;
    this.addChild(this.jars, this.panels);
    if (controls.portrait) {
      const pad = matchPad(controls, false);
      pad.position.set(0, 1040);
      this.addChild(pad);
    }
  }

  render(data: DuelData): void {
    const { mine, theirs } = data;
    // Реплика босса по ходу гонки: удар по нему или «почти побеждён».
    if (mine.boss) {
      if (this.lastHp !== null && mine.boss.hp < this.lastHp) {
        this.taunt = mine.boss.hp * 10 <= mine.boss.maxHp * 3 ? 'low' : 'hit';
      }
      this.lastHp = mine.boss.hp;
    }
    this.renderJars(data);
    const key = JSON.stringify([
      mine.score,
      mine.boss?.hp,
      mine.boss?.abilities.map((a) => a.countdown),
      mine.abilities,
      mine.effects,
      mine.incoming,
      mine.next,
      mine.virusesRemaining,
      theirs?.score,
      theirs?.boss?.hp,
      theirs?.effects,
      theirs?.virusesRemaining,
      data.opponent?.connected,
      data.opponent?.name,
      this.taunt,
    ]);
    if (key === this.panelsKey) return;
    this.panelsKey = key;
    this.stops.splice(0).forEach((stop) => stop());
    this.panels.removeChildren().forEach((child) => child.destroy({ children: true }));
    if (this.controls.portrait) {
      this.portraitPanels(data);
      return;
    }
    this.left(data);
    this.middle(data);
    this.right(data);
  }

  private renderJars(data: DuelData): void {
    const { mine, theirs } = data;
    if (!this.myJar) {
      const rect = this.myRect;
      const cell = fitCell(
        mine.width,
        mine.height,
        rect.w,
        rect.h,
        this.controls.portrait ? 46 : 34,
      );
      const size = jarSize(mine.width, mine.height, cell);
      this.myJar = new JarView(cell);
      this.myJar.position.set(rect.x + (rect.w - size.width) / 2, rect.y);
      this.jars.addChild(this.myJar);
      // Жесты на своей банке: касание — поворот, ведение — сдвиг, взмах вниз — сброс.
      this.stopGestures = attachGestures(
        this.myJar,
        new Rectangle(0, 0, size.width, size.height),
        cell,
        this.controls.onCommand,
      );
    }
    renderBoardJar(this.myJar, mine, data.skin);
    if (theirs) {
      if (!this.theirJar) {
        const rect = this.theirRect;
        const cell = fitCell(theirs.width, theirs.height, rect.w, rect.h, 26);
        this.theirJar = new JarView(cell);
        this.theirJar.position.set(rect.x, rect.y);
        this.jars.addChild(this.theirJar);
      }
      // Скинов соперника сервер пока не присылает — его банка в стандартном виде.
      renderBoardJar(this.theirJar, theirs, null);
    }
  }

  private left(data: DuelData): void {
    const { mine } = data;
    const col = new Container();
    col.position.set(40, 92);
    const w = 252;
    const tag = this.playerTag(
      'Игрок 1',
      COLOR.teal,
      COLOR.ink,
      data.me ? `${data.me.name} (вы)` : 'Вы',
    );
    col.addChild(tag);
    let y = 44;

    const score = new Container();
    score.y = y;
    score.addChild(panel({ width: w, height: 76, radius: 16 }));
    const sk = caps('Очки');
    sk.position.set(16, 12);
    const sv = new Text({
      text: formatScore(mine.score),
      style: textStyle({ size: 30, display: true, weight: '900', shadow: 3 }),
    });
    sv.position.set(16, 28);
    score.addChild(sk, sv);
    col.addChild(score);
    y += 76 + 12;

    // Свой босс или микробы, если матч классический.
    const goal = new Container();
    goal.y = y;
    goal.addChild(
      panel({
        width: w,
        height: 70,
        radius: 16,
        top: 0x4a1638,
        bottom: 0x1e0c24,
        border: 0x8a2e5e,
      }),
    );
    const label = new Text({
      text:
        mine.boss && isBossId(mine.boss.id)
          ? `Ваш босс: ${BOSS_NAMES[mine.boss.id]}`
          : 'Осталось микробов',
      style: textStyle({ size: 13, color: 0xe2b8cc }),
    });
    label.position.set(16, 12);
    const value = new Text({
      text: mine.boss ? `${mine.boss.hp} / ${mine.boss.maxHp}` : String(mine.virusesRemaining),
      style: textStyle({ size: 14, display: true, weight: '800' }),
    });
    value.anchor.set(1, 0);
    value.position.set(w - 16, 12);
    goal.addChild(label, value);
    if (mine.boss) {
      const bar = hpBar(mine.boss.hp, mine.boss.maxHp, w - 32, undefined, 20);
      bar.position.set(16, 38);
      goal.addChild(bar);
    }
    col.addChild(goal);
    y += 70 + 12;

    // Способности C и V.
    const abilities = new Container();
    abilities.y = y;
    const rowH = 64;
    const height = 14 + 16 + 8 + mine.abilities.length * rowH + 4;
    abilities.addChild(
      panel({ width: w, height, radius: 16, top: 0x183a5c, bottom: 0x0c1a36, border: 0x7fd8ff }),
    );
    const ak = caps('Способности', 0x7fd8ff);
    ak.position.set(14, 14);
    abilities.addChild(ak);
    mine.abilities.forEach((a, i) => {
      const ry = 38 + i * rowH;
      const ready = a.energy >= a.cost;
      const ring = abilityRing(a.kind, a.energy / a.cost, ready, 26, this.stops);
      ring.position.set(14 + 26, ry + 28);
      const name = new Text({
        text: ABILITY_NAMES[a.kind as keyof typeof ABILITY_NAMES] ?? a.kind,
        style: textStyle({ size: 15, weight: '700' }),
      });
      name.position.set(78, ry + 8);
      const effect =
        a.kind === 'immunity' && mine.effects.immunity
          ? 'Защита наготове'
          : a.kind === 'cryo' && mine.effects.frozen > 0
            ? 'Иней действует'
            : ready
              ? 'Готово'
              : `Заряд ${Math.round((a.energy / a.cost) * 100)}%`;
      const status = new Text({
        text: effect,
        style: textStyle({ size: 12, color: ready ? 0x7fd8ff : COLOR.textSoft }),
      });
      status.position.set(78, ry + 30);
      const cap = keyCap(i === 0 ? 'C' : 'V');
      cap.position.set(w - 14 - cap.width, ry + 14);
      abilities.addChild(ring, name, status, cap);
    });
    col.addChild(abilities);
    y += height + 12;

    const infect = new Container();
    infect.y = y;
    infect.addChild(
      new Graphics()
        .roundRect(0, 0, w, 74, 16)
        .fill({ color: COLOR.panelBottom, alpha: 0.8 })
        .stroke({ width: 2, color: COLOR.border }),
    );
    const ik = caps('Инфицирование', COLOR.gold);
    ik.position.set(14, 12);
    const it = new Text({
      text: 'Цепочка ×2 и больше отправляет сопернику микробов',
      style: textStyle({ size: 13, color: 0xd6ecf5, wrap: w - 28, lineHeight: 18 }),
    });
    it.position.set(14, 32);
    infect.addChild(ik, it);
    col.addChild(infect);
    this.panels.addChild(col);

    // «Следующая» и предупреждение о входящей атаке над своей банкой.
    const next = new Container();
    const title = caps('Следующая');
    const pieces = capsuleRow(
      mine.next.slice(0, 3),
      26,
      mine.effects.frozen > 0,
      data.skin?.capsule === 'candy',
    );
    const nw = 12 + title.width + 10 + pieces.width + 12;
    next.addChild(
      new Graphics()
        .roundRect(0, 0, nw, 36, 12)
        .fill({ color: COLOR.panelBottom, alpha: 0.85 })
        .stroke({ width: 2, color: COLOR.border }),
    );
    title.anchor.set(0, 0.5);
    title.position.set(12, 18);
    pieces.position.set(12 + title.width + 10, 5);
    next.addChild(title, pieces);
    next.position.set(MY_JAR.x + (MY_JAR.w - nw) / 2, 100);
    this.panels.addChild(next);

    const attack = mine.incoming[0];
    const fever = mine.effects.fever > 0;
    if (attack || fever) {
      const warn = new Container();
      const text = attack
        ? attack.kind === 'fever'
          ? `Входящая атака: Лихорадка · ${(attack.inMs / 1000).toFixed(1).replace('.', ',')} с`
          : `Входящая атака: Инфицирование ×${attack.amount} · ${(attack.inMs / 1000).toFixed(1).replace('.', ',')} с`
        : 'Лихорадка: ваши капсулы падают быстрее';
      const t = new Text({ text, style: textStyle({ size: 14, weight: '700', color: 0xffc9a8 }) });
      const ww = Math.max(330, t.width + 64);
      warn.addChild(
        new Graphics()
          .roundRect(0, 0, ww, 40, 12)
          .fill(0x3a1408)
          .stroke({ width: 2, color: 0xff7a3d }),
      );
      const icon = artSprite('icon-ability-fever', 20);
      icon.position.set(24, 20);
      t.anchor.set(0, 0.5);
      t.position.set(44, 20);
      warn.addChild(icon, t);
      warn.position.set(MY_JAR.x + (MY_JAR.w - ww) / 2, 100);
      this.panels.addChild(warn);
      next.visible = false;
    }
  }

  private middle(data: DuelData): void {
    const { mine, theirs } = data;
    const col = new Container();
    col.position.set(660, 150);
    const w = 200;
    let y = 0;
    if (mine.boss && isBossId(mine.boss.id)) {
      const bubble = speechBubble(BOSS_NAMES[mine.boss.id], BOSS_TAUNTS[this.taunt], 200, 'bottom');
      bubble.position.set((w - bubble.width) / 2, 0);
      col.addChild(bubble);
      this.stops.push(idle(bubble, 'talk'));
      y = bubble.height + 34;
    } else {
      y = 40;
    }
    // Ромб «VS».
    const vs = new Container();
    vs.position.set(w / 2, y + 60);
    const diamond = new Graphics()
      .roundRect(-56, -56, 112, 112, 24)
      .fill(COLOR.ink)
      .roundRect(-52, -52, 104, 104, 22)
      .fill(vertical(COLOR.teal, COLOR.pinkDark));
    diamond.rotation = Math.PI / 4;
    const vt = new Text({
      text: 'VS',
      style: textStyle({ size: 34, display: true, weight: '900', shadow: 3 }),
    });
    vt.anchor.set(0.5);
    vs.addChild(diamond, vt);
    col.addChild(vs);
    y += 150;

    // Гонка: кто ближе к победе (босс или очистка банки).
    const race = caps(mine.boss ? 'Здоровье боссов' : 'Очистка банок', COLOR.textSoft, 10);
    race.anchor.set(0.5, 0);
    race.position.set(w / 2, y);
    col.addChild(race);
    y += 24;
    const bars: [string, number, number][] = [
      ['ВЫ', 1 - raceProgress(mine), COLOR.teal],
      ['СОП.', theirs ? 1 - raceProgress(theirs) : 1, 0xff4f7b],
    ];
    bars.forEach(([label, left, color], i) => {
      const x = w / 2 - 50 + i * 72;
      const g = new Graphics()
        .roundRect(x, y, 30, 10 * 15 + 8, 8)
        .fill({ color: 0x04121a, alpha: 0.8 })
        .stroke({ width: 2, color: COLOR.borderDim });
      const filled = Math.round(left * 10);
      for (let k = 0; k < 10; k++) {
        const by = y + 4 + (9 - k) * 15;
        g.roundRect(x + 4, by, 22, 12, 3);
        if (k < filled) g.fill(color);
        else g.fill({ color: COLOR.white, alpha: 0.06 });
      }
      const t = caps(label, i === 0 ? COLOR.text : 0xffb3c8, 11);
      t.anchor.set(0.5, 0);
      t.position.set(x + 15, y + 10 * 15 + 16);
      col.addChild(g, t);
    });
    y += 10 * 15 + 44;
    const note = new Text({
      text: mine.boss
        ? 'Победит тот, кто первым одолеет своего босса'
        : 'Победит тот, кто первым очистит банку',
      style: textStyle({ size: 12, color: COLOR.textMuted, wrap: w, align: 'center' }),
    });
    note.anchor.set(0.5, 0);
    note.position.set(w / 2, y);
    col.addChild(note);
    if (mine.boss) {
      const hint = new Text({
        text: BOSS_DAMAGE_HINTS[mine.boss.placement],
        style: textStyle({ size: 12, color: 0xe2b8cc, wrap: w, align: 'center' }),
      });
      hint.anchor.set(0.5, 0);
      hint.position.set(w / 2, y + note.height + 10);
      col.addChild(hint);
    }
    this.panels.addChild(col);
  }

  private right(data: DuelData): void {
    const { theirs, opponent } = data;
    const col = new Container();
    col.position.set(890, 92);
    const status = opponent ? (opponent.connected ? 'В игре' : 'Нет связи') : 'Ждём';
    col.addChild(
      this.playerTag(
        'Игрок 2',
        0xff4f7b,
        COLOR.white,
        opponent?.name ?? 'Соперник',
        status,
        opponent?.connected ?? false,
      ),
    );
    if (!theirs) {
      this.panels.addChild(col);
      return;
    }
    // Иммунитет соперника: светлая рамка вокруг его банки.
    if (theirs.effects.immunity && this.theirJar) {
      const jw = jarSize(theirs.width, theirs.height, this.theirJar.cellSize).width;
      const jh = jarSize(theirs.width, theirs.height, this.theirJar.cellSize).height;
      const ring = new Graphics()
        .roundRect(-6, 28, jw + 12, jh - 22, 30)
        .stroke({ width: 3, color: 0x9ef2c9, alpha: 0.9 });
      ring.position.set(THEIR_JAR.x - 890, THEIR_JAR.y - 92);
      col.addChild(ring);
    }
    const sx = THEIR_JAR.w + 20;
    const sw = 510 - sx;
    let y = 40;
    const box = (height: number, top: number, bottom: number, border: number) => {
      const c = new Container();
      c.position.set(sx, y);
      c.addChild(
        new Graphics()
          .roundRect(0, 0, sw, height, 14)
          .fill(vertical(top, bottom))
          .stroke({ width: 2, color: border }),
      );
      y += height + 10;
      col.addChild(c);
      return c;
    };
    const score = box(64, 0x0e1c3a, 0x0e1c3a, COLOR.borderDim);
    const sl = new Text({ text: 'Очки', style: textStyle({ size: 12, color: COLOR.textMuted }) });
    sl.position.set(14, 10);
    const sv = new Text({
      text: formatScore(theirs.score),
      style: textStyle({ size: 20, display: true, weight: '800' }),
    });
    sv.position.set(14, 28);
    score.addChild(sl, sv);

    const goal = box(70, 0x4a1638, 0x2a0c24, 0x5e2346);
    const gl = new Text({
      text:
        theirs.boss && isBossId(theirs.boss.id)
          ? `Его ${BOSS_NAMES[theirs.boss.id]} · ${theirs.boss.hp} / ${theirs.boss.maxHp}`
          : `Осталось микробов: ${theirs.virusesRemaining}`,
      style: textStyle({ size: 12, color: 0xe2b8cc }),
    });
    gl.position.set(14, 10);
    goal.addChild(gl);
    if (theirs.boss) {
      const bar = hpBar(theirs.boss.hp, theirs.boss.maxHp, sw - 28, undefined, 18);
      bar.position.set(14, 34);
      goal.addChild(bar);
    }

    const effect = (icon: string, title: string, text: string, top: number, border: number) => {
      const c = box(62, top, top, border);
      const i = artSprite(icon, 26);
      i.position.set(14 + 13, 31);
      const t = new Text({ text: title, style: textStyle({ size: 14, weight: '700' }) });
      t.position.set(50, 10);
      const d = new Text({ text, style: textStyle({ size: 12, color: 0xc9f5e5, wrap: sw - 64 }) });
      d.position.set(50, 32);
      c.addChild(i, t, d);
    };
    if (theirs.effects.immunity)
      effect('icon-ability-immunity', 'Иммунитет', 'Отразит одну вашу атаку', 0x143c32, 0x3fa488);
    if (theirs.effects.fever > 0)
      effect('icon-ability-fever', 'Лихорадка', 'Его капсулы падают быстрее', 0x3a1408, 0xa04a22);
    if (theirs.effects.frozen > 0)
      effect('icon-ability-cryo', 'Криотерапия', 'Его капсула заморожена', 0x183a5c, 0x7fd8ff);
    this.panels.addChild(col);
  }

  private playerTag(
    label: string,
    color: number,
    textColor: number,
    name: string,
    status?: string,
    online = true,
  ): Container {
    const tag = new Container();
    const t = caps(label, textColor, 12);
    const bw = t.width + 20;
    tag.addChild(
      new Graphics()
        .roundRect(0, 3, bw, 28, 8)
        .fill(COLOR.ink)
        .roundRect(0, 0, bw, 28, 8)
        .fill(color),
    );
    t.position.set(10, 7);
    const n = new Text({
      text: name,
      style: textStyle({ size: 16, display: true, weight: '800' }),
    });
    n.position.set(bw + 10, 2);
    tag.addChild(t, n);
    if (status) {
      const s = caps(status, online ? COLOR.mint : COLOR.gold, 11);
      s.position.set(bw + 10 + n.width + 12, 8);
      tag.addChild(s);
    }
    return tag;
  }

  /** Телефон: сводка сверху, справа под банкой соперника — гонка и кнопки способностей. */
  private portraitPanels(data: DuelData): void {
    const { mine, theirs, opponent } = data;
    this.panels.addChild(hudTile(22, 200, 'Очки', formatScore(mine.score)));
    const goal = hudTile(
      234,
      236,
      mine.boss ? 'Ваш босс' : 'Микробы',
      mine.boss ? `${mine.boss.hp}/${mine.boss.maxHp}` : String(mine.virusesRemaining),
      mine.boss ? COLOR.pink : COLOR.mint,
    );
    if (mine.boss) {
      goal.children.at(-1)?.scale.set(0.75);
      const bar = hpBar(mine.boss.hp, mine.boss.maxHp, 120, undefined, 18);
      bar.position.set(104, 46);
      goal.addChild(bar);
    }
    this.panels.addChild(goal);
    const opp = hudTile(
      482,
      216,
      opponent ? (opponent.connected ? opponent.name : 'Нет связи') : 'Соперник',
      theirs ? formatScore(theirs.score) : '—',
      0xffb3c8,
    );
    const oppTitle = opp.children[1];
    if (oppTitle && oppTitle.width > 188) oppTitle.scale.set(188 / oppTitle.width);
    this.panels.addChild(opp);

    // «Следующая» или входящая атака над своей банкой.
    const attack = mine.incoming[0];
    const row = new Container();
    row.position.set(22, 192);
    if (attack || mine.effects.fever > 0) {
      const text = attack
        ? `Атака: ${attack.kind === 'fever' ? 'Лихорадка' : `Инфицирование ×${attack.amount}`} · ${(attack.inMs / 1000).toFixed(1).replace('.', ',')} с`
        : 'Лихорадка: капсулы быстрее';
      const t = new Text({ text, style: textStyle({ size: 15, weight: '700', color: 0xffc9a8 }) });
      row.addChild(
        new Graphics()
          .roundRect(0, 0, 498, 40, 12)
          .fill(0x3a1408)
          .stroke({ width: 2, color: 0xff7a3d }),
      );
      t.anchor.set(0, 0.5);
      t.position.set(16, 20);
      row.addChild(t);
    } else {
      const title = caps('Следующая');
      const pieces = capsuleRow(
        mine.next.slice(0, 3),
        26,
        mine.effects.frozen > 0,
        data.skin?.capsule === 'candy',
      );
      const nw = 12 + title.width + 10 + pieces.width + 12;
      row.addChild(
        new Graphics()
          .roundRect(0, 0, nw, 40, 12)
          .fill({ color: COLOR.panelBottom, alpha: 0.85 })
          .stroke({ width: 2, color: COLOR.border }),
      );
      title.anchor.set(0, 0.5);
      title.position.set(12, 20);
      pieces.position.set(12 + title.width + 10, 7);
      row.addChild(title, pieces);
    }
    this.panels.addChild(row);

    // Справа: гонка и кольца способностей — тоже кнопки.
    const col = new Container();
    const jarH = this.theirJar
      ? jarSize(theirs?.width ?? 8, theirs?.height ?? 16, this.theirJar.cellSize).height
      : 0;
    col.position.set(530, 240 + jarH + 14);
    const bars: [string, number, number][] = [
      ['Вы', 1 - raceProgress(mine), COLOR.teal],
      ['Соп.', theirs ? 1 - raceProgress(theirs) : 1, 0xff4f7b],
    ];
    bars.forEach(([label, left, color], i) => {
      const t = caps(label, i === 0 ? COLOR.text : 0xffb3c8, 11);
      t.position.set(0, i * 30 + 4);
      const g = new Graphics()
        .roundRect(48, i * 30, 120, 20, 6)
        .fill({ color: 0x04121a, alpha: 0.8 })
        .roundRect(48, i * 30, 120 * left, 20, 6)
        .fill(color);
      col.addChild(t, g);
    });
    mine.abilities.forEach((a, i) => {
      const ready = a.energy >= a.cost;
      const ring = abilityRing(a.kind, a.energy / a.cost, ready, 40, this.stops);
      ring.position.set(84, 110 + i * 112);
      ring.eventMode = 'static';
      ring.cursor = 'pointer';
      ring.accessible = true;
      ring.accessibleType = 'button';
      const name = ABILITY_NAMES[a.kind as keyof typeof ABILITY_NAMES] ?? a.kind;
      ring.accessibleTitle = `${name}${ready ? ', готово' : ''}`;
      ring.on('pointertap', () => this.controls.onAbility(i === 0 ? 0 : 1));
      const t = new Text({ text: name, style: textStyle({ size: 13, weight: '700' }) });
      t.anchor.set(0.5, 0);
      t.position.set(84, 110 + i * 112 + 46);
      if (t.width > 168) t.scale.set(168 / t.width);
      col.addChild(ring, t);
    });
    this.panels.addChild(col);

    if (theirs?.effects.immunity && this.theirJar) {
      const size = jarSize(theirs.width, theirs.height, this.theirJar.cellSize);
      this.panels.addChild(
        new Graphics()
          .roundRect(530 - 4, 240 + 28, size.width + 8, size.height - 24, 22)
          .stroke({ width: 3, color: 0x9ef2c9, alpha: 0.9 }),
      );
    }
  }

  override destroy(): void {
    this.stopGestures?.();
    this.stops.splice(0).forEach((stop) => stop());
    super.destroy({ children: true });
  }
}
